import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  addDeviceToMonitoring,
  removeDeviceFromMonitoring,
} from "../../store/devicesSlice";
import { selectAllUserMaps } from "../../store/mapsSlice";
import { IDevice } from "../../types";
import SignalStrengthIndicator from "./SignalStrengthIndicator";

interface DeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  device: IDevice | null;
}

const DeviceModal: React.FC<DeviceModalProps> = ({
  isOpen,
  onClose,
  device,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { language } = useSelector((state: RootState) => state.ui);
  const maps = useSelector(selectAllUserMaps);
  const [activeTab, setActiveTab] = useState<"edit" | "info">("info");
  const [formData, setFormData] = useState({
    displayName: "",
    color: "#3b82f6",
    icon: "default",
    showTrail: false,
    showOnMaps: [] as string[],
    positioningMode: "auto" as "static" | "mobile" | "auto",
  });

  // Update form data when device changes
  useEffect(() => {
    if (device) {
      setFormData({
        displayName: device.customSettings?.displayName || device.name || "",
        color: device.customSettings?.color || "#3b82f6",
        icon: device.customSettings?.icon || "default",
        showTrail: device.customSettings?.showTrail || false,
        showOnMaps: device.customSettings?.showOnMaps || [],
        positioningMode: device.positioningMode || "auto",
      });
    }
  }, [device]);

  if (!isOpen || !device) return null;

  const handleSave = async () => {
    try {
      const customSettings = {
        displayName: formData.displayName,
        color: formData.color,
        icon: formData.icon,
        showTrail: formData.showTrail,
        showOnMaps: formData.showOnMaps,
      };

      // Update device with custom settings
      await dispatch(
        addDeviceToMonitoring({
          mac: device.mac,
          settings: customSettings,
        })
      );

      // Update positioning mode separately via API
      if (formData.positioningMode !== device.positioningMode) {
        const { ApiService } = await import("../../services/api");
        const apiService = new ApiService(
          process.env.REACT_APP_API_URL || "",
          process.env.REACT_APP_API_URL || "http://localhost:3051/api"
        );
        await apiService.updateDevice(device.mac, {
          positioningMode: formData.positioningMode,
        });
      }

      onClose();
    } catch (error) {
      console.error("Failed to update device:", error);
    }
  };

  const handleRemoveFromMonitoring = async () => {
    if (
      window.confirm(
        language === "he"
          ? "האם אתה בטוח שברצונך להסיר התקן זה מהמעקב?"
          : "Are you sure you want to remove this device from monitoring?"
      )
    ) {
      try {
        await dispatch(removeDeviceFromMonitoring(device.mac));
        onClose();
      } catch (error) {
        console.error("Failed to remove device from monitoring:", error);
      }
    }
  };

  const formatLastSeen = (lastSeen?: string) => {
    if (!lastSeen) return language === "he" ? "לא זמין" : "N/A";
    const date = new Date(lastSeen);
    return date.toLocaleString();
  };

  const getConfidenceLabel = (confidence?: number) => {
    if (confidence === undefined) return language === "he" ? "לא זמין" : "N/A";
    const percentage = Math.round(confidence * 100);
    if (confidence > 0.8)
      return `${percentage}% (${language === "he" ? "גבוה" : "High"})`;
    if (confidence > 0.5)
      return `${percentage}% (${language === "he" ? "בינוני" : "Medium"})`;
    return `${percentage}% (${language === "he" ? "נמוך" : "Low"})`;
  };

  const tabs = [
    { id: "info" as const, label: language === "he" ? "מידע" : "Info" },
    { id: "edit" as const, label: language === "he" ? "עריכה" : "Edit" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {device.customSettings?.displayName || device.name || device.mac}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">{device.mac}</p>
        </div>

        {/* Tabs */}
        <div className="px-6 py-3 border-b border-gray-200">
          <div className="flex space-x-1 rtl:space-x-reverse">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-96 overflow-y-auto">
          {activeTab === "info" && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  {language === "he" ? "מידע בסיסי" : "Basic Information"}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {language === "he" ? "שם התקן" : "Device Name"}
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {device.customSettings?.displayName ||
                        device.name ||
                        device.mac}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {language === "he" ? "כתובת MAC" : "MAC Address"}
                    </label>
                    <p className="mt-1 text-sm text-gray-900 font-mono">
                      {device.mac}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {language === "he" ? "סטטוס" : "Status"}
                    </label>
                    <p
                      className={`mt-1 text-sm font-medium ${
                        device.lastSeen &&
                        Date.now() - new Date(device.lastSeen).getTime() <
                          180000
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {device.lastSeen &&
                      Date.now() - new Date(device.lastSeen).getTime() < 180000
                        ? language === "he"
                          ? "מחובר"
                          : "Online"
                        : language === "he"
                        ? "לא מחובר"
                        : "Offline"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      {language === "he" ? "נראה לאחרונה" : "Last Seen"}
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatLastSeen(device.lastSeen)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Position Information */}
              {device.position && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    {language === "he" ? "מיקום" : "Position"}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he" ? "קואורדינטות" : "Coordinates"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">
                        ({device.position.x.toFixed(1)},{" "}
                        {device.position.y.toFixed(1)})
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he"
                          ? "שער חזק ביותר"
                          : "Strongest Gateway"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {device.strongestGateway ||
                          (language === "he" ? "לא זמין" : "N/A")}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Signal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  {language === "he" ? "מידע אות" : "Signal Information"}
                </h3>
                <div className="space-y-3">
                  {device.rssi && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he"
                          ? "עוצמת אות (RSSI)"
                          : "Signal Strength (RSSI)"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {device.rssi}dBm
                      </p>
                    </div>
                  )}
                  {device.signal && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {language === "he"
                          ? "חוזק אות מפורט"
                          : "Detailed Signal Strength"}
                      </label>
                      <SignalStrengthIndicator
                        mac={device.mac}
                        signals={device.signal}
                        className="text-sm"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Triangulation Information */}
              {device.triangulationInfo && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    {language === "he"
                      ? "מידע טריאנגולציה"
                      : "Triangulation Information"}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he" ? "שיטה" : "Method"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {device.triangulationInfo.method.replace("_", " ")}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he" ? "ביטחון" : "Confidence"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {getConfidenceLabel(
                          device.triangulationInfo.confidence
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he" ? "אותות בשימוש" : "Signals Used"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {device.triangulationInfo.signalsUsed}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he" ? "שער ראשי" : "Primary Gateway"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {device.triangulationInfo.primaryGateway ||
                          (language === "he" ? "לא זמין" : "N/A")}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {language === "he" ? "חושב לאחרונה" : "Last Calculated"}
                      </label>
                      <p className="mt-1 text-sm text-gray-900">
                        {new Date(
                          device.triangulationInfo.lastCalculated
                        ).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "edit" && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === "he" ? "שם תצוגה" : "Display Name"}
                </label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={(e) =>
                    setFormData({ ...formData, displayName: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    language === "he" ? "הזן שם תצוגה" : "Enter display name"
                  }
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === "he" ? "צבע" : "Color"}
                </label>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) =>
                      setFormData({ ...formData, color: e.target.value })
                    }
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    placeholder="#3b82f6"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === "he" ? "מצב מיקום" : "Positioning Mode"}
                </label>
                <select
                  value={formData.positioningMode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      positioningMode: e.target.value as
                        | "static"
                        | "mobile"
                        | "auto",
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="auto">
                    {language === "he" ? "🔄 אוטומטי" : "🔄 Auto"}
                  </option>
                  <option value="static">
                    {language === "he" ? "📍 סטטי" : "📍 Static"}
                  </option>
                  <option value="mobile">
                    {language === "he" ? "🚶 נייד" : "🚶 Mobile"}
                  </option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {language === "he"
                    ? "אוטומטי: המערכת תזהה אוטומטית | סטטי: למכשירים קבועים | נייד: למכשירים נעים"
                    : "Auto: System detects automatically | Static: For fixed devices | Mobile: For moving devices"}
                </p>
              </div>

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === "he" ? "סמל" : "Icon"}
                </label>
                <select
                  value={formData.icon}
                  onChange={(e) =>
                    setFormData({ ...formData, icon: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="default">
                    {language === "he" ? "ברירת מחדל" : "Default"}
                  </option>
                  <option value="phone">
                    {language === "he" ? "טלפון" : "Phone"}
                  </option>
                  <option value="laptop">
                    {language === "he" ? "מחשב נייד" : "Laptop"}
                  </option>
                  <option value="tablet">
                    {language === "he" ? "טאבלט" : "Tablet"}
                  </option>
                  <option value="watch">
                    {language === "he" ? "שעון" : "Watch"}
                  </option>
                </select>
              </div> */}

              {/* <div>
                <label className="flex items-center space-x-2 rtl:space-x-reverse">
                  <input
                    type="checkbox"
                    checked={formData.showTrail}
                    onChange={(e) =>
                      setFormData({ ...formData, showTrail: e.target.checked })
                    }
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {language === "he"
                      ? "הצג עקבות תנועה"
                      : "Show movement trail"}
                  </span>
                </label>
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {language === "he" ? "הצג במפות" : "Show on Maps"}
                </label>
                <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {maps.map((map) => (
                    <label
                      key={map._id}
                      className="flex items-center space-x-2 rtl:space-x-reverse py-1"
                    >
                      <input
                        type="checkbox"
                        checked={formData.showOnMaps.includes(map._id)}
                        onChange={(e) => {
                          const newShowOnMaps = e.target.checked
                            ? [...formData.showOnMaps, map._id]
                            : formData.showOnMaps.filter(
                                (id) => id !== map._id
                              );
                          setFormData({
                            ...formData,
                            showOnMaps: newShowOnMaps,
                          });
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{map.name}</span>
                    </label>
                  ))}
                </div>
              </div> */}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={handleRemoveFromMonitoring}
            className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
          >
            {language === "he" ? "הסר ממעקב" : "Remove from Monitoring"}
          </button>
          <div className="space-x-3 rtl:space-x-reverse">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
            >
              {language === "he" ? "ביטול" : "Cancel"}
            </button>
            {activeTab === "edit" && (
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                {language === "he" ? "שמור" : "Save"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceModal;
